/**
 * Typed hooks for the Accounting V2 expense API (/api/accounting/expenses).
 *
 * The legacy expense screen talks to /api/accounts/expenses and is left alone;
 * these hooks serve the enterprise capabilities added on top (categories,
 * refunds, reimbursements, corporate cards, recurrence, billing, reports).
 *
 * All V2 accounting endpoints wrap payloads in { ok, data, meta }, so every hook
 * unwraps `.data.data` and surfaces `meta` where pagination matters.
 *
 * MONEY: every *Minor field is an integer in the currency's minor unit (paise).
 * Never render one directly; divide by 100 for INR at the display layer.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

// ── Envelope ────────────────────────────────────────────────────────────────

interface Envelope<T> {
  ok: boolean;
  data: T;
  meta?: PageMeta;
}

export interface PageMeta {
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  count?: number;
}

const ROOT = '/api/accounting/expenses';

/** Drop empty values so blank filter inputs do not narrow a query. */
function toParams(filters?: Record<string, unknown>): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch a list and GUARANTEE an array back.
 *
 * The server has a SPA catch-all (app.get('*')), so an unmatched GET /api/...
 * answers 200 with index.html rather than 404. Without this coercion `items`
 * would be a string or object and any `.map` in a component would throw, taking
 * the page down behind the error boundary instead of showing an empty state.
 */
async function getList<T>(path: string, filters?: Record<string, unknown>) {
  const { data } = await api.get<Envelope<T[]>>(`${ROOT}${path}${toParams(filters)}`);
  const payload = data?.data;
  return {
    items: Array.isArray(payload) ? payload : [],
    meta: data?.meta && typeof data.meta === 'object' ? data.meta : {},
  };
}

/**
 * Fetch a single object, or null when the payload is not usable.
 *
 * Returns NULL rather than undefined on purpose: React Query rejects undefined
 * as query data and throws "Query data cannot be undefined", which would trade
 * one white-screen for another. Callers use optional chaining or a null check.
 */
async function getOne<T>(path: string, filters?: Record<string, unknown>) {
  const { data } = await api.get<Envelope<T>>(`${ROOT}${path}${toParams(filters)}`);
  const payload = data?.data;
  return (payload && typeof payload === 'object' ? payload : null) as T | null;
}

// ── Domain types ────────────────────────────────────────────────────────────

export type ExpenseStatus =
  | 'draft' | 'submitted' | 'approved' | 'rejected'
  | 'posted' | 'partially_paid' | 'paid' | 'cancelled' | 'refunded';

export type ExpensePaymentStatus = 'unpaid' | 'partially_paid' | 'paid';

export type ExpenseSettlementMode =
  | 'paid_cash' | 'paid_bank' | 'corporate_card' | 'employee_paid' | 'supplier_credit';

export type ExpenseTaxTreatment =
  | 'exclusive' | 'inclusive' | 'zero_rated' | 'exempt' | 'reverse_charge';

export interface ExpenseAttachment {
  _id?: string;
  kind: 'receipt' | 'invoice' | 'pdf' | 'image' | 'document' | 'other';
  fileName: string;
  fileUrl: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadedAt?: string;
}

export interface Expense {
  _id: string;
  expenseNo: string;
  referenceNumber?: string;
  status: ExpenseStatus;
  expenseDate: string;
  postingDate?: string | null;
  description?: string;
  notes?: string;

  supplierId?: string;
  supplierName?: string;
  employeeId?: string;
  employeeName?: string;
  departmentId?: string;
  costCenterId?: string;
  projectId?: string | null;
  campaignId?: string;
  locationId?: string | null;

  categoryId?: string | null;
  categoryLabel?: string;

  currency: string;
  exchangeRate: number;
  netAmountMinor: number;
  discountMinor: number;
  taxRate: number;
  taxTreatment: ExpenseTaxTreatment;
  taxAmountMinor: number;
  withholdingTaxMinor: number;
  grossAmountMinor: number;
  markupPercent: number;
  isBillable: boolean;
  isReimbursable: boolean;

  settlementMode: ExpenseSettlementMode;
  paymentStatus: ExpensePaymentStatus;
  paymentMethod: string;
  paidAmountMinor: number;
  outstandingMinor: number;
  refundedAmountMinor: number;

  generatedByRecurrence?: boolean;
  journalEntryId?: string | null;
  currentApprovalLevel?: number;
  requiredApprovalLevels?: number;
  rejectionReason?: string;
  attachments?: ExpenseAttachment[];
  createdBy: string;
  createdAt?: string;
}

export interface ExpenseCategory {
  _id: string;
  code: string;
  name: string;
  parentId?: string | null;
  expenseAccountCode?: string;
  defaultTaxRate: number;
  defaultTaxTreatment: ExpenseTaxTreatment;
  isCapital: boolean;
  isBillableByDefault: boolean;
  isReimbursableByDefault: boolean;
  requiresReceipt: boolean;
  maxAmountMinor: number;
  isActive: boolean;
  children?: ExpenseCategory[];
}

export interface ExpenseApproval {
  _id: string;
  level: number;
  approverUserId?: string;
  approverRole?: string;
  decision: 'pending' | 'approved' | 'rejected' | 'delegated' | 'skipped';
  comments?: string;
  decidedAt?: string | null;
  delegatedFromUserId?: string;
}

export interface ExpenseRefund {
  _id: string;
  refundNo: string;
  originalExpenseId: string;
  refundDate: string;
  netAmountMinor: number;
  taxAmountMinor: number;
  grossAmountMinor: number;
  isPartial: boolean;
  reason: string;
  reasonNote?: string;
}

export interface ExpensePayment {
  _id: string;
  paymentNo: string;
  paymentDate: string;
  amountMinor: number;
  paymentMethod: string;
  transactionReference?: string;
}

export interface ExpenseRecurrence {
  _id: string;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  startDate: string;
  endDate?: string | null;
  nextRunDate: string;
  lastRunDate?: string | null;
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  retryCount: number;
  maxRetries: number;
  lastError?: string;
  occurrencesGenerated: number;
  maxOccurrences: number;
}

export interface RecurrenceExecution {
  _id: string;
  periodKey: string;
  runAt: string;
  outcome: 'created' | 'skipped' | 'failed';
  error?: string;
  attempt: number;
}

export interface ReimbursementBalance {
  employeeId: string;
  employeeName?: string;
  outstandingMinor: number;
  expenseCount: number;
}

export interface EmployeeReimbursement {
  _id: string;
  reimbursementNo: string;
  employeeId: string;
  employeeName?: string;
  reimbursementDate: string;
  amountMinor: number;
  status: 'pending' | 'paid' | 'cancelled';
  paymentMethod: string;
  expenseIds: string[];
}

export interface CorporateCardBalance {
  cardId: string;
  cardName: string;
  lastFourDigits?: string;
  holderName?: string;
  creditLimitMinor: number;
  outstandingMinor: number;
  unsettledCount: number;
  isActive: boolean;
}

export interface CardTransaction {
  _id: string;
  transactionDate: string;
  merchantName?: string;
  amountMinor: number;
  externalReference?: string;
  matchStatus: 'unmatched' | 'matched' | 'ignored' | 'settled';
  expenseId?: string | null;
}

export interface BillableLink {
  _id: string;
  expenseId: string;
  projectId?: string | null;
  customerId?: string;
  costMinor: number;
  markupPercent: number;
  billableMinor: number;
  status: 'pending' | 'wip' | 'invoiced' | 'written_off';
  invoiceNo?: string;
}

// ── Reports ─────────────────────────────────────────────────────────────────

export interface ExpenseSummary {
  totals: {
    netMinor: number;
    taxMinor: number;
    grossMinor: number;
    outstandingMinor: number;
    paidMinor: number;
    refundedMinor: number;
    expenseCount: number;
  };
  byStatus: Array<{ status: string; grossMinor: number; count: number }>;
  bySettlementMode: Array<{ settlementMode: string; grossMinor: number; count: number }>;
}

export interface DimensionRow {
  label: string;
  netMinor: number;
  taxMinor: number;
  grossMinor: number;
  outstandingMinor: number;
  expenseCount: number;
  sharePercent: number;
}

export interface ExpenseDashboard {
  todaySpendMinor: number;
  monthSpendMinor: number;
  monthExpenseCount: number;
  outstandingMinor: number;
  pendingApprovalCount: number;
  topCategories: DimensionRow[];
  topVendors: DimensionRow[];
  byDepartment: DimensionRow[];
  trend: Array<{ period: string; grossMinor: number; netMinor: number; expenseCount: number }>;
  ageing: Array<{ bucket: string; settlementMode: string; outstandingMinor: number; expenseCount: number }>;
  recurringDueSoon: ExpenseRecurrence[];
  recurringFailing: ExpenseRecurrence[];
}

export interface GlReconciliation {
  documents: { netMinor: number; taxMinor: number; expenseCount: number };
  ledger: {
    netMinor: number;
    accounts: Array<{ accountCode: string; accountName: string; debitMinor: number; creditMinor: number; netMinor: number }>;
  };
  varianceMinor: number;
  reconciled: boolean;
  note: string;
}

// ── Filters & payloads ──────────────────────────────────────────────────────

export interface ExpenseFilters {
  status?: string;
  paymentStatus?: string;
  settlementMode?: string;
  categoryId?: string;
  departmentId?: string;
  costCenterId?: string;
  employeeId?: string;
  supplierId?: string;
  projectId?: string;
  currency?: string;
  isBillable?: boolean | '';
  isReimbursable?: boolean | '';
  isRecurring?: boolean | '';
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateExpensePayload {
  expenseDate: string;
  netAmountMinor: number;
  discountMinor?: number;
  taxRate?: number;
  taxTreatment?: ExpenseTaxTreatment;
  withholdingRate?: number;
  currency?: string;
  exchangeRate?: number;
  categoryId?: string;
  categoryLabel?: string;
  description?: string;
  referenceNumber?: string;
  notes?: string;
  supplierId?: string;
  supplierName?: string;
  employeeId?: string;
  employeeName?: string;
  departmentId?: string;
  costCenterId?: string;
  projectId?: string;
  campaignId?: string;
  paymentMethod?: string;
  settlementMode?: ExpenseSettlementMode;
  isBillable?: boolean;
  isReimbursable?: boolean;
  markupPercent?: number;
  attachments?: ExpenseAttachment[];
  submit?: boolean;
}

// ── Query keys ──────────────────────────────────────────────────────────────

const KEY = 'accounting-expenses';

/** Invalidate every expense query at once after a mutation. */
function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [KEY] });
}

// ── Expenses ────────────────────────────────────────────────────────────────

export function useExpensesV2(filters?: ExpenseFilters) {
  return useQuery({
    queryKey: [KEY, 'list', filters],
    queryFn: () => getList<Expense>('/', filters as Record<string, unknown>),
  });
}

export function useExpenseDetail(id: string | null) {
  return useQuery({
    queryKey: [KEY, 'detail', id],
    enabled: !!id,
    queryFn: () =>
      getOne<{ expense: Expense; approvals: ExpenseApproval[]; refunds: ExpenseRefund[]; payments: ExpensePayment[] }>(
        `/${id}`,
      ),
  });
}

export function useCreateExpenseV2() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: CreateExpensePayload) =>
      (await api.post<Envelope<Expense>>(ROOT, payload)).data.data,
    onSuccess: invalidate,
  });
}

export function useUpdateExpenseV2() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<CreateExpensePayload> }) =>
      (await api.patch<Envelope<Expense>>(`${ROOT}/${id}`, payload)).data.data,
    onSuccess: invalidate,
  });
}

export function useDeleteExpenseV2() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete<Envelope<unknown>>(`${ROOT}/${id}`)).data.data,
    onSuccess: invalidate,
  });
}

/**
 * Lifecycle actions share one hook because they are all
 * POST /:id/<action> with an optional body.
 */
export type ExpenseAction = 'submit' | 'approve' | 'reject' | 'post' | 'pay' | 'cancel' | 'billable';

export function useExpenseAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, action, body }: { id: string; action: ExpenseAction; body?: Record<string, unknown> }) =>
      (await api.post<Envelope<unknown>>(`${ROOT}/${id}/${action}`, body ?? {})).data.data,
    onSuccess: invalidate,
  });
}

// ── Chart of accounts (for pickers) ─────────────────────────────────────────

export interface PostableAccount {
  _id: string;
  code: string;
  name: string;
  type: string;
  isGroupHeader?: boolean;
  isControlAccount?: boolean;
  isSystem?: boolean;
  isActive?: boolean;
}

/**
 * Expense accounts a category or expense may actually post to.
 *
 * Reuses the existing GET /api/accounting/accounts rather than adding a second
 * chart-of-accounts endpoint. Group headers, control accounts and system
 * accounts are filtered out here because the posting engine and the category
 * validator both refuse them — offering them in a picker would only produce a
 * server-side rejection the user cannot act on.
 *
 * Returns [] rather than throwing when the caller lacks COA_VIEW, so a picker
 * can fall back to manual entry instead of breaking the form.
 */
export function usePostableExpenseAccounts() {
  return useQuery({
    queryKey: [KEY, 'postable-expense-accounts'],
    queryFn: async () => {
      try {
        const { data } = await api.get<Envelope<PostableAccount[]>>(
          '/api/accounting/accounts?type=expense&isActive=true&pageSize=200',
        );
        const rows = Array.isArray(data?.data) ? data.data : [];
        return rows.filter(
          (a) => !a.isGroupHeader && !a.isControlAccount && !a.isSystem && a.isActive !== false,
        );
      } catch {
        return [] as PostableAccount[];
      }
    },
  });
}

// ── Categories ──────────────────────────────────────────────────────────────

export function useExpenseCategories(filters?: { isActive?: boolean | ''; asTree?: boolean }) {
  return useQuery({
    queryKey: [KEY, 'categories', filters],
    queryFn: () => getList<ExpenseCategory>('/categories', filters as Record<string, unknown>),
  });
}

export function useSaveExpenseCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: Partial<ExpenseCategory> }) =>
      id
        ? (await api.patch<Envelope<ExpenseCategory>>(`${ROOT}/categories/${id}`, payload)).data.data
        : (await api.post<Envelope<ExpenseCategory>>(`${ROOT}/categories`, payload)).data.data,
    onSuccess: invalidate,
  });
}

export function useArchiveExpenseCategory() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<Envelope<ExpenseCategory>>(`${ROOT}/categories/${id}/archive`, {})).data.data,
    onSuccess: invalidate,
  });
}

// ── Approvals ───────────────────────────────────────────────────────────────

export function useExpenseApprovalInbox() {
  return useQuery({
    queryKey: [KEY, 'approval-inbox'],
    queryFn: () => getList<{ approval: ExpenseApproval; expense: Expense }>('/approvals/inbox'),
  });
}

// ── Refunds ─────────────────────────────────────────────────────────────────

export function useExpenseRefunds(filters?: { reason?: string; startDate?: string; endDate?: string; page?: number }) {
  return useQuery({
    queryKey: [KEY, 'refunds', filters],
    queryFn: () => getList<ExpenseRefund>('/refunds', filters),
  });
}

export function useRecordRefund() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: {
      expenseId: string;
      grossAmountMinor: number;
      reason?: string;
      reasonNote?: string;
      refundDate?: string;
    }) => (await api.post<Envelope<unknown>>(`${ROOT}/refunds`, payload)).data.data,
    onSuccess: invalidate,
  });
}

// ── Recurrence ──────────────────────────────────────────────────────────────

export function useExpenseRecurrences(filters?: { status?: string; frequency?: string }) {
  return useQuery({
    queryKey: [KEY, 'recurring', filters],
    queryFn: () => getList<ExpenseRecurrence>('/recurring', filters),
  });
}

export function useRecurrenceExecutions(id: string | null) {
  return useQuery({
    queryKey: [KEY, 'recurring', id, 'executions'],
    enabled: !!id,
    queryFn: () => getList<RecurrenceExecution>(`/recurring/${id}/executions`),
  });
}

export function useCreateRecurrence() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      frequency: string;
      startDate: string;
      endDate?: string;
      dayOfPeriod?: number;
      maxOccurrences?: number;
      template: CreateExpensePayload;
    }) => (await api.post<Envelope<ExpenseRecurrence>>(`${ROOT}/recurring`, payload)).data.data,
    onSuccess: invalidate,
  });
}

export type RecurrenceAction = 'run' | 'pause' | 'resume' | 'cancel';

export function useRecurrenceAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: RecurrenceAction }) =>
      (await api.post<Envelope<unknown>>(`${ROOT}/recurring/${id}/${action}`, {})).data.data,
    onSuccess: invalidate,
  });
}

/** Runs every due profile. Safe to call repeatedly; occurrences claim once. */
export function useRunDueRecurrences() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async () =>
      (await api.post<Envelope<{ processed: number; created: number; skipped: number; failed: number }>>(
        `${ROOT}/recurring/run-due`,
        {},
      )).data.data,
    onSuccess: invalidate,
  });
}

// ── Reimbursements ──────────────────────────────────────────────────────────

export function useReimbursements(filters?: { status?: string; employeeId?: string }) {
  return useQuery({
    queryKey: [KEY, 'reimbursements', filters],
    queryFn: () => getList<EmployeeReimbursement>('/reimbursements', filters),
  });
}

export function useReimbursementBalances() {
  return useQuery({
    queryKey: [KEY, 'reimbursement-balances'],
    queryFn: () => getList<ReimbursementBalance>('/reimbursements/balances'),
  });
}

export function useCreateReimbursement() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: { employeeId: string; expenseIds: string[]; paymentMethod?: string }) =>
      (await api.post<Envelope<EmployeeReimbursement>>(`${ROOT}/reimbursements`, payload)).data.data,
    onSuccess: invalidate,
  });
}

export function usePayReimbursement() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
      (await api.post<Envelope<unknown>>(`${ROOT}/reimbursements/${id}/pay`, body ?? {})).data.data,
    onSuccess: invalidate,
  });
}

// ── Corporate cards ─────────────────────────────────────────────────────────

export function useCorporateCards() {
  return useQuery({
    queryKey: [KEY, 'cards'],
    queryFn: () => getList<CorporateCardBalance>('/cards'),
  });
}

export function useCardTransactions(filters?: { cardId?: string; matchStatus?: string; page?: number }) {
  return useQuery({
    queryKey: [KEY, 'card-transactions', filters],
    queryFn: () => getList<CardTransaction>('/cards/transactions', filters),
  });
}

export function useCreateCard() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: {
      cardName: string;
      lastFourDigits?: string;
      issuer?: string;
      holderName?: string;
      creditLimitMinor?: number;
    }) => (await api.post<Envelope<unknown>>(`${ROOT}/cards`, payload)).data.data,
    onSuccess: invalidate,
  });
}

export function useSettleCard() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
      (await api.post<Envelope<{ settlementNo: string; amountMinor: number; transactionsSettled: number }>>(
        `${ROOT}/cards/${id}/settle`,
        body ?? {},
      )).data.data,
    onSuccess: invalidate,
  });
}

// ── Billable ────────────────────────────────────────────────────────────────

export function useBillableLinks(filters?: { status?: string; projectId?: string }) {
  return useQuery({
    queryKey: [KEY, 'billable', filters],
    queryFn: () => getList<BillableLink>('/billable', filters),
  });
}

// ── Reports ─────────────────────────────────────────────────────────────────

export function useExpenseDashboard(months = 6) {
  return useQuery({
    queryKey: [KEY, 'dashboard', months],
    queryFn: () => getOne<ExpenseDashboard>('/dashboard', { months }),
  });
}

export function useExpenseSummary(filters?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: [KEY, 'report', 'summary', filters],
    queryFn: () => getOne<ExpenseSummary>('/reports/summary', filters),
  });
}

export type ReportDimension =
  | 'category' | 'vendor' | 'employee' | 'project'
  | 'department' | 'costCenter' | 'campaign' | 'currency';

export function useExpensesByDimension(
  dimension: ReportDimension,
  filters?: { startDate?: string; endDate?: string; limit?: number },
) {
  return useQuery({
    queryKey: [KEY, 'report', 'by', dimension, filters],
    queryFn: () =>
      getOne<{ dimension: string; totalGrossMinor: number; rows: DimensionRow[] }>(
        `/reports/by/${dimension}`,
        filters,
      ),
  });
}

export function useExpenseAgeing() {
  return useQuery({
    queryKey: [KEY, 'report', 'ageing'],
    queryFn: () =>
      getOne<Array<{ bucket: string; settlementMode: string; outstandingMinor: number; expenseCount: number }>>(
        '/reports/ageing',
      ),
  });
}

export function useExpenseTaxReport(filters?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: [KEY, 'report', 'tax', filters],
    queryFn: () =>
      getOne<{
        rows: Array<{
          taxTreatment: string; taxRate: number; netMinor: number;
          taxMinor: number; withholdingMinor: number; expenseCount: number; isRecoverable: boolean;
        }>;
        totals: { recoverableTaxMinor: number; totalTaxMinor: number; withholdingMinor: number };
      }>('/reports/tax', filters),
  });
}

export function useGlReconciliation(filters?: { startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: [KEY, 'report', 'gl-reconciliation', filters],
    queryFn: () => getOne<GlReconciliation>('/reports/gl-reconciliation', filters),
  });
}
